import type { FormField } from '@strata-monorepo/tracking/src/types'
import type { FormStepResult } from '../types'
import type { FmpFormStep } from '../../types/form'
import flattenObject from '@strata-monorepo/utils/flattenObject'

type CurrentStepProps = Pick<FmpFormStep, 'componentProps' | 'schema'>

/**
 * Gets a value from a nested object using dot notation
 * @param obj - Object to traverse
 * @param path - Dot-notation path (e.g., 'homeInfo.homeSize')
 * @returns The value at the path, or undefined if not found
 */
const getNestedValue = (obj: FormStepResult, path: string): unknown => {
  const keys = path.split('.')
  let current: unknown = obj

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key]
    } else {
      return undefined
    }
  }

  return current
}

/**
 * Processes a single field value and adds it to the fields array
 * Handles arrays, objects (flattening them), and primitive values
 *
 * @param fields - Array to push field entries into
 * @param fieldName - Name of the field
 * @param fieldValue - Value of the field
 */
const processFieldValue = (fields: FormField[], fieldName: string, fieldValue: unknown): void => {
  // Skip empty, null, or undefined values
  if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
    return
  }

  // Handle array values (checkboxes, multiple selections)
  if (Array.isArray(fieldValue)) {
    if (fieldValue.length > 0) {
      fieldValue.forEach((value) => {
        const stringValue =
          typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)

        fields.push({
          fieldName,
          fieldLabel: fieldName,
          fieldValue: stringValue,
        })
      })
    }
  } else if (typeof fieldValue === 'object') {
    // Handle object values (address, complex data structures) - flatten them
    const flattened = flattenObject(fieldValue as Record<string, unknown>, fieldName)
    flattened.forEach(({ key, value }) => {
      fields.push({
        fieldName: key,
        fieldLabel: key,
        fieldValue: value,
      })
    })
  } else {
    // Handle primitive values (text, radio, select, numbers, booleans)
    fields.push({
      fieldName,
      fieldLabel: fieldName,
      fieldValue: String(fieldValue),
    })
  }
}

/**
 * Builds a field array for tracking from the current step's form fields
 * Extracts field names from the current step schema and gets their values from formResults
 * Flattens nested objects into dot-notation field names
 *
 * @param currentStep - The current step configuration from fmpFormFlow
 * @param formResults - The current form results from the store
 * @returns Array of FormField objects with fieldName and fieldValue for current step only
 */
export const buildFieldArrayFromFormResults = (
  currentStep: CurrentStepProps,
  formResults: FormStepResult,
): FormField[] => {
  const fields: FormField[] = []
  const fieldNames: Set<string> = new Set()

  // Collect field names from the current step
  const componentProps = currentStep.componentProps || {}
  const formValue = componentProps.formValue as string | undefined
  const textCollection = componentProps.textCollection as Array<{ formValue: string }> | undefined

  // Add formValue field (single field like radio, select)
  if (formValue) {
    fieldNames.add(formValue)
  }

  // Add textCollection fields (multiple text inputs)
  if (textCollection) {
    textCollection.forEach((field) => {
      fieldNames.add(field.formValue)
    })
  }

  // Get field names from schema if available
  if (currentStep.schema?._def?.shape) {
    const shape = currentStep.schema._def.shape()
    Object.keys(shape).forEach((key) => {
      fieldNames.add(key)
    })
  }

  // Build field array only for fields belonging to this step
  fieldNames.forEach((fieldName) => {
    // Handle dot-notation field names (e.g., 'homeInfo.homeSize')
    const fieldValue = fieldName.includes('.')
      ? getNestedValue(formResults, fieldName)
      : formResults[fieldName]

    processFieldValue(fields, fieldName, fieldValue)
  })

  return fields
}

/**
 * Builds a field array for tracking from all form results
 * Processes all fields in formResults and flattens nested objects into dot-notation field names
 *
 * @param formResults - The complete form results from the store
 * @returns Array of FormField objects with fieldName and fieldValue for all fields
 */
export const buildFieldArrayFromFinalFormResults = (formResults: FormStepResult): FormField[] => {
  const fields: FormField[] = []

  Object.entries(formResults).forEach(([fieldName, fieldValue]) => {
    processFieldValue(fields, fieldName, fieldValue)
  })

  return fields
}
